"use client";

import { useApp } from "@/contexts/AppContext";
import PublicHeader from "@/components/PublicHeader";
import Footer from "@/components/Footer";
import HomeMealFeed from "@/components/HomeMealFeed";
import Image from "next/image";
import {
  FaHome,
  FaLeaf,
  FaGlobeAmericas,
  FaMapMarkerAlt,
  FaCreditCard,
  FaHeart,
  FaUtensils,
} from "react-icons/fa";
import { MdLooksTwo, MdLooks3, MdLooksOne } from "react-icons/md";
import {
  PageWrapper,
  HeroSection,
  HeroContent,
  HeroText,
  HeroTitle,
  HeroSubtitle,
  HeroButtons,
  HeroButton,
  HeroImageContainer,
  HeroImage,
  FeaturesSection,
  SectionTitle,
  SectionSubtitle,
  FeaturesGrid,
  FeatureCard,
  FeatureIcon,
  FeatureHeader,
  FeatureTitle,
  FeatureDescription,
  CalloutSection,
  CalloutContent,
  CalloutImage,
  CalloutText,
  CalloutTitle,
  CalloutDescription,
  CalloutList,
  CalloutListItem,
  CalloutButton,
  CTASection,
  CTAContent,
  CTATitle,
  CTASubtitle,
  CTAButtons,
  CTAButton,
} from "./page.styles";

export default function HomePage() {
  const { user } = useApp();

  return (
    <PageWrapper>
      {/* Navigation Header */}
      <PublicHeader />

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroText>
            <HeroTitle>
              Enjoy Delicious Meals from Local Home Artisans
            </HeroTitle>
            <HeroSubtitle>
              Discover authentic, homemade cuisine crafted with love by talented
              artisans in your neighborhood
            </HeroSubtitle>
            <HeroButtons>
              {user ? (
                <>
                  <HeroButton href="/feed">
                    <FaUtensils style={{ marginRight: "8px" }} /> Browse Meals
                  </HeroButton>
                  <HeroButton $variant="secondary" href="/feed">
                    ✨ Explore Feed
                  </HeroButton>
                </>
              ) : (
                <>
                  <HeroButton href="/register">Get Started</HeroButton>
                  <HeroButton $variant="secondary" href="/login">
                    Login
                  </HeroButton>
                </>
              )}
            </HeroButtons>
          </HeroText>
          <HeroImageContainer>
            <HeroImage />
          </HeroImageContainer>
        </HeroContent>
      </HeroSection>

      {/* Top-Rated Meals Feed - Show for all users */}
      <HomeMealFeed />

      {/* Features Section */}
      <FeaturesSection>
        <SectionTitle>Why Choose Drizl?</SectionTitle>
        <SectionSubtitle>
          Experience the best of local cuisine with our unique platform
        </SectionSubtitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaHome size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Local Home Artisans</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Discover amazing meals from talented local artisans who cook with
              passion and care
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaLeaf size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Fresh & Local Ingredients</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Enjoy meals made with fresh, locally-sourced ingredients for
              authentic flavors
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaGlobeAmericas size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Diverse Cuisines</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Explore authentic dishes from around the world, made by artisans
              from your community
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaMapMarkerAlt size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Convenient Delivery</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Get fresh food delivered right to your door or pick it up at your
              convenience
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaCreditCard size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Secure Payments</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Safe and easy payment options with complete buyer and seller
              protection
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureHeader>
              <FeatureIcon>
                <FaHeart size={48} color="#C8553D" />
              </FeatureIcon>
              <FeatureTitle>Support Local</FeatureTitle>
            </FeatureHeader>
            <FeatureDescription>
              Help local artisans grow their business and strengthen your
              community
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      {/* Taste Local Flavor Callout */}
      <CalloutSection>
        <CalloutContent>
          <CalloutImage>
            <Image
              src="/images/callout1.jpg"
              alt="Delicious homemade meal"
              fill
              style={{ objectFit: "cover" }}
              priority
            />
          </CalloutImage>
          <CalloutText>
            <CalloutTitle>Taste the Local Flavor</CalloutTitle>
            <CalloutDescription>
              Experience authentic homemade meals just like grandma used to
              make! Our artisans bring generations of culinary tradition and
              passion to every dish.
            </CalloutDescription>
            <CalloutList>
              <CalloutListItem>
                Authentic recipes passed down through generations
              </CalloutListItem>
              <CalloutListItem>
                Made with love and attention to detail
              </CalloutListItem>
              <CalloutListItem>
                Unique flavors you won&apos;t find in restaurants
              </CalloutListItem>
              <CalloutListItem>
                Support your neighborhood artisans
              </CalloutListItem>
            </CalloutList>
            <CalloutButton href="/feed">Explore Meals</CalloutButton>
          </CalloutText>
        </CalloutContent>
      </CalloutSection>

      {/* Fresh and Local Callout */}
      <CalloutSection $bgColor="white" $reverse>
        <CalloutContent $reverse>
          <CalloutImage>
            <Image
              src="/images/callout2.jpg"
              alt="Fresh locally sourced ingredients"
              fill
              style={{ objectFit: "cover" }}
              priority
            />
          </CalloutImage>
          <CalloutText>
            <CalloutTitle>Fresh and Locally Sourced</CalloutTitle>
            <CalloutDescription>
              Our artisans take pride in using the freshest, locally-sourced
              ingredients to create delicious meals that taste amazing and
              support local farmers.
            </CalloutDescription>
            <CalloutList>
              <CalloutListItem>
                Fresh ingredients sourced from local markets
              </CalloutListItem>
              <CalloutListItem>
                Support your local economy and small businesses
              </CalloutListItem>
              <CalloutListItem>
                Sustainable and environmentally friendly
              </CalloutListItem>
              <CalloutListItem>
                Healthier options with no preservatives
              </CalloutListItem>
            </CalloutList>
            <CalloutButton href="/feed">See What&apos;s Fresh</CalloutButton>
          </CalloutText>
        </CalloutContent>
      </CalloutSection>

      {/* For Consumers CTA */}
      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Discover Amazing Meals?</CTATitle>
          <CTASubtitle>
            Join thousands of food lovers who have discovered the joy of
            ordering from local home artisans
          </CTASubtitle>
          <CTAButtons>
            <CTAButton href="/register">Sign Up Now</CTAButton>
            <CTAButton $variant="secondary" href="/feed">
              Browse Meals
            </CTAButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>

      {/* For Artisans Section */}
      <CalloutSection id="for-artisans">
        <CalloutContent>
          <CalloutImage>
            <Image
              src="/images/artisan-header.jpg"
              alt="Home artisan cooking"
              fill
              style={{ objectFit: "cover" }}
              priority
            />
          </CalloutImage>
          <CalloutText>
            <CalloutTitle>Become an Artisan on Drizl</CalloutTitle>
            <CalloutDescription>
              Turn your passion for cooking into a thriving business. Share your
              culinary creations with your community and earn income doing what
              you love!
            </CalloutDescription>
            <CalloutList>
              <CalloutListItem>
                Set your own prices and schedule
              </CalloutListItem>
              <CalloutListItem>
                Work from the comfort of your home
              </CalloutListItem>
              <CalloutListItem>Connect with local food lovers</CalloutListItem>
              <CalloutListItem>
                Build your reputation and grow your business
              </CalloutListItem>
              <CalloutListItem>
                Simple tools to manage orders and customers
              </CalloutListItem>
            </CalloutList>
            <CalloutButton href="/register">Start Cooking Today</CalloutButton>
          </CalloutText>
        </CalloutContent>
      </CalloutSection>

      {/* How It Works Section */}
      <FeaturesSection id="how-it-works">
        <SectionTitle>How It Works</SectionTitle>
        <SectionSubtitle>
          Getting started is easy! Follow these simple steps
        </SectionSubtitle>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIcon>
              <MdLooksOne size={48} color="#C8553D" />
            </FeatureIcon>
            <FeatureTitle>Browse Meals</FeatureTitle>
            <FeatureDescription>
              Explore delicious homemade meals from local artisans in your area
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>
              <MdLooksTwo size={48} color="#C8553D" />
            </FeatureIcon>
            <FeatureTitle>Place Your Order</FeatureTitle>
            <FeatureDescription>
              Select your favorite meals, customize options, and place your
              order securely
            </FeatureDescription>
          </FeatureCard>
          <FeatureCard>
            <FeatureIcon>
              <MdLooks3 size={48} color="#C8553D" />
            </FeatureIcon>
            <FeatureTitle>Enjoy Your Meal</FeatureTitle>
            <FeatureDescription>
              Pick up or get it delivered fresh to your door, and enjoy
              authentic homemade food!
            </FeatureDescription>
          </FeatureCard>
        </FeaturesGrid>
      </FeaturesSection>

      <Footer />
    </PageWrapper>
  );
}
